import React from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import { modulePriceLabel } from './costsFormat'

export const moduleAnchor = (sectionId, moduleId) => `${sectionId}-module-${moduleId}`

// "A, B or C" in the reader's language (alternatives of a dependency).
const orList = (names, lang) => {
  try {
    return new Intl.ListFormat(lang, { type: 'disjunction' }).format(names)
  } catch {
    return names.join(' / ')
  }
}

/**
 * One module of the price list: name, monthly price (or "contact"), what it does and, as
 * links to the other cards, the modules it works with.
 *
 * The description is content, not code: `costs:modules.<id>.description`, shown only
 * when the translation exists. Dependencies come from the server (MODULE_REQUIRES).
 *
 * `image` (optional, `{ src, alt }`): a picture above the name. No module has one yet and
 * the card is designed to stand without it; adding them is ticket
 * 6ad9db16e13e5eeed86b140b ("Portal 'Info & costs': add an image next to each module card").
 */
const ModuleCard = ({ module, sectionId, tone, neededBy = [], lang, image = null }) => {
  const { t, i18n } = useTranslation('costs')
  const moduleName = (id) => t(`payments:subscription.modules.${id}`, { defaultValue: id })
  const name = moduleName(module.id)
  // i18next returns the key for a missing text, even with an empty default: ask first.
  const descriptionKey = `modules.${module.id}.description`
  const description = i18n?.exists?.(`costs:${descriptionKey}`) ? t(descriptionKey) : ''
  const audienceKey = `modules.${module.id}.audience`
  const audience = i18n?.exists?.(`costs:${audienceKey}`) ? t(audienceKey) : ''
  const requires = (Array.isArray(module.requires) ? module.requires : [])
    .map((req) => ({ ...req, anyOf: Array.isArray(req?.anyOf) ? req.anyOf : [] }))
    .filter((req) => req.anyOf.length > 0)

  return (
    <li
      id={moduleAnchor(sectionId, module.id)}
      data-testid={`costs-module-${module.id}`}
      className={clsx(
        'group flex flex-col gap-3 rounded-2xl p-6 scroll-mt-24 target:ring-2 target:ring-bright-blue/60',
        tone.card,
        tone.cardHover
      )}
    >
      {image?.src && (
        <img
          src={image.src}
          alt={image.alt || name}
          loading="lazy"
          data-testid={`costs-module-${module.id}-image`}
          className="aspect-[16/9] w-full rounded-xl object-cover"
        />
      )}
      {/* The price pill always sits under the name: the same place on every card,
          whatever the length of the name or the width of the card. */}
      <div className="flex flex-col items-start gap-2">
        <h4 className={clsx('min-w-0 text-lg font-semibold leading-snug', tone.text)}>{name}</h4>
        <span
          data-testid={`costs-module-${module.id}-price`}
          className={clsx(
            'max-w-full rounded-full px-3 py-1 text-xs font-medium',
            tone.pill
          )}
        >
          {t(...modulePriceLabel(module, lang))}
        </span>
      </div>
      {description && (
        <p
          data-testid={`costs-module-${module.id}-description`}
          className={clsx('text-sm font-light leading-relaxed', tone.muted)}
        >
          {description}
        </p>
      )}
      {audience && (
        <p data-testid={`costs-module-${module.id}-audience`} className={clsx('text-xs leading-relaxed', tone.muted)}>
          <span className={clsx('mr-2 uppercase tracking-[0.14em]', tone.faint)}>
            {t('modules.audienceLabel')}
          </span>
          {audience}
        </p>
      )}
      {(requires.length > 0 || neededBy.length > 0) && (
        <ul className="mt-auto flex flex-wrap gap-2" aria-label={t('modules.dependencies')}>
          {requires.map((req) => {
            const names = orList(req.anyOf.map(moduleName), lang)
            const label = t(`modules.requires.${req.feature}`, {
              module: names,
              defaultValue: t('modules.requires.generic', { module: names }),
            })
            const chip = clsx('inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs', tone.chip)
            const testId = `costs-module-${module.id}-requires-${req.anyOf.join('-')}`
            return (
              <li key={`r-${req.feature}-${req.anyOf.join('-')}`}>
                {/* One module: a link to its card. Alternatives: a plain label. */}
                {req.anyOf.length === 1 ? (
                  <a href={`#${moduleAnchor(sectionId, req.anyOf[0])}`} data-testid={testId} className={chip}>
                    <span aria-hidden="true">↳</span>
                    {label}
                  </a>
                ) : (
                  <span data-testid={testId} className={chip}>
                    <span aria-hidden="true">↳</span>
                    {label}
                  </span>
                )}
              </li>
            )
          })}
          {neededBy.map((other) => (
            <li key={`n-${other}`}>
              <a
                href={`#${moduleAnchor(sectionId, other)}`}
                data-testid={`costs-module-${module.id}-neededby-${other}`}
                className={clsx(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs',
                  tone.chipQuiet
                )}
              >
                <span aria-hidden="true">↗</span>
                {t('modules.neededBy', { module: moduleName(other) })}
              </a>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

export default ModuleCard
